import {
  AccountType,
  getV1CompaniesCompanyIdAccountsAccountIdAutoAddOnAssignments,
  getV1CompaniesCompanyIdDriversDriverIdAutoAddOnAssignments,
  RateOwnerType,
} from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { createContext } from 'react';

import { DEFAULT_API_PAGE_SIZE } from '~constants/consts';
import { AddOnAssignment } from '~hooks/useAddOns/models';
import useCursorPaginationQuery from '~queries/useCursorPaginationQuery';
import { extractPagination, Pagination, PaginationLink } from '~services/pagination';
import reportRequestError from '~services/reportRequestError';

type AutomaticAddonsQueryContextType = {
  data: AddOnAssignment[];
  goToPage: (link?: PaginationLink) => void;
  isFetching: boolean;
  pagination: Pagination;
  reloadCurrentPage: () => Promise<void>;
  startOver: () => Promise<void>;
};

const AutomaticAddonsQueryContext = createContext<AutomaticAddonsQueryContextType>({
  data: [],
  goToPage: () => {},
  isFetching: false,
  pagination: { limit: DEFAULT_API_PAGE_SIZE, page: 1 },
  reloadCurrentPage: async () => {},
  startOver: async () => {},
});

interface AutomaticAddonsQueryContextProviderProps {
  companyId: string;
  ownerId?: string;
  ownerType?: RateOwnerType;
}

function AutomaticAddonsQueryContextProvider({
  children,
  companyId,
  ownerId,
  ownerType,
}: React.PropsWithChildren<AutomaticAddonsQueryContextProviderProps>) {
  const { goToPage, isFetching, pagination, reloadCurrentPage, rows, startOver } =
    useCursorPaginationQuery<AddOnAssignment>({
      fetchPage: async ({ cursor, limit, link, signal }) => {
        if (!ownerId || !ownerType) {
          return { data: [], pagination: { limit, page: 1 } };
        }

        const query = {
          'page[limit]': limit,
          ...(link && cursor ? { [`page[${link}]`]: cursor } : {}),
        };

        try {
          let response;
          switch (ownerType) {
            case RateOwnerType.DRIVER:
              response = await getV1CompaniesCompanyIdDriversDriverIdAutoAddOnAssignments(
                {
                  path: { 'company-id': companyId, 'driver-id': ownerId },
                  query,
                  signal,
                },
              );
              break;
            case RateOwnerType.VENDOR:
              response =
                await getV1CompaniesCompanyIdAccountsAccountIdAutoAddOnAssignments({
                  path: { 'company-id': companyId, 'account-id': ownerId },
                  query: { 'filter[account_type]': AccountType.VENDOR, ...query },
                  signal,
                });
              break;
            case RateOwnerType.CUSTOMER:
              response =
                await getV1CompaniesCompanyIdAccountsAccountIdAutoAddOnAssignments({
                  path: { 'company-id': companyId, 'account-id': ownerId },
                  query: { 'filter[account_type]': AccountType.CUSTOMER, ...query },
                  signal,
                });
              break;
            default:
              throw new Error(`Unsupported owner type: ${ownerType}`);
          }

          return {
            data: response.data.data.map((addOnAssignment) =>
              AddOnAssignment.parse(addOnAssignment),
            ),
            pagination: extractPagination(response),
          };
        } catch (error) {
          if (!signal.aborted) {
            reportRequestError(
              error,
              t('error_messages.auto_add_on_assignments.failed_to_fetch'),
            );
          }
          throw error;
        }
      },
      limit: DEFAULT_API_PAGE_SIZE,
      queryKey: ['automaticAddOnAssignments', companyId, ownerType, ownerId],
    });

  return (
    <AutomaticAddonsQueryContext.Provider
      value={{
        data: rows,
        goToPage,
        isFetching,
        pagination,
        reloadCurrentPage,
        startOver,
      }}
    >
      {children}
    </AutomaticAddonsQueryContext.Provider>
  );
}

export default AutomaticAddonsQueryContext;
export { AutomaticAddonsQueryContextProvider };
